import React, { useState } from "react";
import { X, Camera, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { formatINR } from "@/lib/api";

// "Place order on behalf of a retailer" dialog: pick shop, search + add products, adjust quantities.
// Scanning: the camera button opens the QR scanner; a USB scanner (or typing a piece
// code / SKU) + Enter in the search box does the same. Each scanned tag is listed under
// its line so staff can see exactly which physical pieces were pulled for the order.
export const NewOrderDialog = ({
  open, onClose, shops, selectedShop, setSelectedShop, productQuery, setProductQuery, productMatches,
  onAddItem, orderItems, updateQty, removeItem, estimatedTotal, creatingOrder, onSubmit, onScan,
}) => {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const runScan = async (code) => {
    const raw = (code || "").trim();
    if (!raw || !onScan) return;
    setScanning(true);
    try {
      const ok = await onScan(raw);
      if (ok !== false) setProductQuery("");
    } finally { setScanning(false); }
  };
  const handleScan = async (code) => {
    setScannerOpen(false);
    await runScan(code);
  };
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="new-order-dialog" className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>New Order — On Behalf of Retailer</DialogTitle>
        <DialogDescription>Select a retailer and add products to place a wholesale order on their behalf. It will still need normal approval.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Shop</Label>
          <Select value={selectedShop} onValueChange={setSelectedShop}>
            <SelectTrigger data-testid="new-order-shop-select" className="mt-1"><SelectValue placeholder="Select a shop" /></SelectTrigger>
            <SelectContent>
              {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.shop_name} · {s.phone}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Add Products</Label>
          <div className="relative mt-1">
            <Input data-testid="new-order-product-search" value={productQuery} onChange={(e) => setProductQuery(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter" && productQuery.trim()) { e.preventDefault(); runScan(productQuery); } }}
                   placeholder="Scan QR or enter SKU / name" className="pr-10" disabled={scanning} />
            <button data-testid="new-order-scan-button" type="button" onClick={() => setScannerOpen(true)}
                    title="Scan QR with camera" aria-label="Scan QR with camera"
                    className="absolute right-1 top-1 h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[#F7F0E6] transition-colors">
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Tap the camera to scan a tag, or scan with a USB reader / type a piece code and press Enter.</p>
          {productMatches.length > 0 && (
            <div className="mt-1 border border-border rounded-lg max-h-40 overflow-y-auto">
              {productMatches.map((p) => (
                <button key={p.id} type="button" data-testid="new-order-product-result" onClick={() => { onAddItem(p); setProductQuery(""); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F7F0E6] text-left transition-colors duration-150">
                  <span>{p.name} <span className="text-muted-foreground font-mono text-xs">{p.sku}</span></span>
                  <span className="font-mono">{formatINR(p.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {orderItems.length > 0 && (
          <div className="border border-border rounded-lg divide-y divide-border" data-testid="new-order-items-list">
            {orderItems.map((i) => (
              <div key={i.product_id} className="px-3 py-2 text-sm" data-testid="new-order-item-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0"><div className="truncate">{i.name}</div><div className="text-xs text-muted-foreground font-mono">{i.sku}</div></div>
                  <Input data-testid="new-order-item-qty" type="number" value={i.quantity} onChange={(e) => updateQty(i.product_id, Number(e.target.value))} className="w-16 h-8 text-center" />
                  <span className="font-mono w-24 text-right">{formatINR(i.price * i.quantity)}</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeItem(i.product_id)}><X className="h-4 w-4" /></Button>
                </div>
                {i.barcodes?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid="new-order-item-scanned">
                    <QrCode className="h-3 w-3 text-muted-foreground" />
                    {i.barcodes.map((b) => (
                      <Badge key={b} variant="secondary" className="font-mono text-[10px] px-1.5 py-0 bg-[#F7F0E6] text-foreground" data-testid="new-order-scanned-piece">{b}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 text-sm font-medium bg-[#F7F0E6]">
              <span>Estimated Subtotal</span><span data-testid="new-order-estimated-total" className="font-mono">{formatINR(estimatedTotal)}</span>
            </div>
            <p className="px-3 py-2 text-xs text-muted-foreground">Final wholesale pricing (with this shop&apos;s discount tier) is applied automatically when the order is placed. It will still need normal approval.</p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="new-order-submit" disabled={creatingOrder || !selectedShop || orderItems.length === 0} onClick={onSubmit}>{creatingOrder ? "Placing..." : "Place Order"}</Button>
      </DialogFooter>
    </DialogContent>
    <BarcodeScanner open={scannerOpen} onOpenChange={setScannerOpen} onDetected={handleScan}
                     title="Scan Tag to Add" description="Point the camera at the QR code on the tag to add that piece to the order. USB scanners and manual entry work too." />
  </Dialog>
  );
};
